import { Tabs as ArkTabs } from "@ark-ui/react"
import cx from "classnames"
import React from "react"

export type TabsPropsType = {
  children?: React.ReactNode
  /**
   * Index of the initial actived tab (first tab by default)
   */
  defaultTabId?: string
  /**
   * Method to be run on tab change
   */
  onChange?: (id: string) => void
  /**
   * Left aligned tabs with no uppercased labels
   */
  subLevel?: boolean
}

type TabPropsType = {
  children?: React.ReactNode
  /**
   * Unique id of the tab
   */
  id: string
  /**
   * Label/title of the tab
   */
  label: string | React.ReactNode
  /**
   * Hypertext link. If provided, the tab will be an anchor
   */
  href?: string
}

// Only for the API, render nothing, we use InternalTab component internally
export const Tab = (_props: TabPropsType) => null
Tab.displayName = "Tab"

const isTabComponent = (
  component: React.ReactNode,
): component is React.ReactElement<TabPropsType> =>
  React.isValidElement(component) && component.type === Tab

const Tabs = ({
  defaultTabId,
  subLevel,
  children,
  onChange,
}: TabsPropsType) => {
  const tabsPropsArray: TabPropsType[] = []
  const tabsChildren: React.ReactNode[] = []
  let matchedHrefId: string | undefined
  React.Children.forEach(children, (child) => {
    if (isTabComponent(child)) {
      const { children: tabChildren, ...tabProps } = child.props
      tabsPropsArray.push(tabProps)
      // if no children, it will be null, nothing will be rendered
      tabsChildren.push(
        <ArkTabs.Content
          value={tabProps.id}
          className="cobalt-tabs__panel"
          key={tabProps.id}
        >
          {tabChildren}
        </ArkTabs.Content>,
      )
      if (tabProps.href === window.location.pathname) {
        matchedHrefId = tabProps.id
      }
    }
  })

  const getOnLinkClick = (href?: string) => {
    if (!href) return
    return () => {
      window.location.pathname = href
    }
  }

  return (
    <ArkTabs.Root
      className="cobalt-tabs"
      defaultValue={
        matchedHrefId || defaultTabId?.toString() || tabsPropsArray?.[0]?.id
      }
      onValueChange={(details) => onChange?.(details.value)}
    >
      <ArkTabs.List
        className={cx("cobalt-tabs__labels", {
          "cobalt-tabs__labels--subLevel": subLevel,
        })}
      >
        {tabsPropsArray.map((props) => (
          <ArkTabs.Trigger
            className="cobalt-tabs__label"
            value={props.id}
            onClick={getOnLinkClick(props.href)}
            key={props.id}
          >
            {props.label}
          </ArkTabs.Trigger>
        ))}
        <ArkTabs.Indicator className="cobalt-tabs__indicator" />
      </ArkTabs.List>
      {tabsChildren}
    </ArkTabs.Root>
  )
}

Tabs.Tab = Tab

export default Tabs
